import Modal from 'react-modal';
import { useDispatch } from 'react-redux';
import OrderDetail from './OrderDetail';
import { useEffect, useState } from 'react';
import { formatDate } from '../../utils/dateUtils';
import { formatAmount } from '../../utils/formatAmount';
import ShipDateDropdown from '../../sharedComponents/ShipDateDropdown'
import ConfirmDialog from '../../sharedComponents/ConfirmDialog'
import { FaCheck, FaTimes, FaExclamationCircle } from 'react-icons/fa';
import { toast } from 'react-toastify';
import useManageOrder from '../order/useManageOrder';
import { useAuth } from '../../context/AuthContext';
import useManageWeeklyShip from '../weeklyShip/useManageWeeklyShip';

const customStyles = {
  overlay: {
    backgroundColor: 'rgba(0, 0, 0, 0.75)', // This sets the overlay color
    zIndex: 1000
  },
  content: {
    top: '5%',
    left: '50%',
    right: 'auto',
    bottom: 'auto',
    width: '1100px',
    maxWidth: '95vw',
    maxHeight: '90vh',
    height: 'auto',
    transform: 'translateX(-50%)',
    overflowY: 'auto',
    overflowX: 'hidden',
  },
};


export default function ProfileOrders({orders, handleSuccessUpdate, accumulatedOrderAmount, ordersPagination, onPageChange, onShipDateChange, selectedShipDateId}) {

  const dispatch = useDispatch()
  const { authState } = useAuth();
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [isFormOpen, setIsFormOpen] = useState(false)
  const [totalAmount, setTotalAmount] = useState(0)
  const [showOrders, setShowOrders] = useState([]);
  const [multiSelect, setMultiSelect] = useState(false);
  const [selectedItems, setSelectedItems] = useState([]);
  const [clientPage, setClientPage] = useState(1);
  const [itemsPerPage] = useState(200);

  const {massUpdate, cancelOrder} = useManageOrder()
  const {getBoxNosFromOrderIds} = useManageWeeklyShip()
  // One shared confirm dialog drives both the per-row and bulk cancel.
  const [confirm, setConfirm] = useState(null); // { message, confirmText, onConfirm }

  // Use server-side pagination if available, otherwise use client-side
  const currentPage = ordersPagination?.currentPage || clientPage;
  const totalPages = ordersPagination?.totalPages || Math.ceil(showOrders.length / itemsPerPage);
  
  // For server-side pagination, use orders directly; for client-side, slice the array
  const indexOfLastItem = clientPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentOrders = ordersPagination ? (orders || []) : showOrders.slice(indexOfFirstItem, indexOfLastItem);

  const paginate = (pageNumber) => {
    if (ordersPagination && onPageChange) {
      onPageChange(pageNumber);
    } else {
      // Client-side pagination (fallback)
      setClientPage(pageNumber);
    }
  };

  const renderPageNumbers = () => {
    let pages = [];
    for (let i = 1; i <= totalPages; i++) {
      pages.push(
        <button
          key={i}
          className={`px-2 py-1 ${currentPage === i ? 'bg-blue-500 text-white' : 'bg-gray-200'}`}
          onClick={() => paginate(i)}
        >
          {i}
        </button>
      );
    }
    return pages;
  };

  useEffect(() => {
    // Only use client-side filtering if pagination is not provided
    if (!ordersPagination) {
      setShowOrders(orders || []);
    }
  }, [orders, ordersPagination]);

  useEffect(() => {
    // Use accumulatedOrderAmount if available and not in multi-select mode
    // Otherwise calculate based on filtered orders
    if (!multiSelect && accumulatedOrderAmount !== undefined && accumulatedOrderAmount !== null) {
      // With server-side pagination, accumulatedOrderAmount from backend reflects filtered results
      if (ordersPagination) {
        setTotalAmount(accumulatedOrderAmount);
      } else {
        // Client-side: use accumulatedOrderAmount if showing all orders, otherwise calculate
        if (showOrders.length === orders.length) {
          setTotalAmount(accumulatedOrderAmount);
        } else {
          // Filtered orders - calculate based on filtered set
          setTotalAmount(calculateTotalFee(showOrders));
        }
      }
    } else {
      // Multi-select mode or no accumulatedOrderAmount - calculate normally
      const ordersToCalculate = ordersPagination ? orders : showOrders;
      setTotalAmount(calculateTotalFee(ordersToCalculate));
    }
  }, [showOrders, multiSelect, accumulatedOrderAmount, orders, ordersPagination]);

  const toggleMultiSelect = () => {
    setMultiSelect(!multiSelect);
    setSelectedItems([]); // Reset selection when toggling mode
    if(multiSelect){
      // When exiting multi-select, use accumulatedOrderAmount if available
      if (accumulatedOrderAmount !== undefined && accumulatedOrderAmount !== null) {
        if (ordersPagination) {
          // Server-side: use accumulatedOrderAmount from backend
          setTotalAmount(accumulatedOrderAmount);
        } else if (showOrders.length === orders.length) {
          // Client-side: use accumulatedOrderAmount if showing all orders
          setTotalAmount(accumulatedOrderAmount);
        } else {
          setTotalAmount(calculateTotalFee(showOrders));
        }
      } else {
        const ordersToCalculate = ordersPagination ? orders : showOrders;
        setTotalAmount(calculateTotalFee(ordersToCalculate));
      }
    }
  }

  const handleSelectItem = (orderId) => {
    setSelectedItems(prev => {
      let newSelectedItems;
      if (prev.includes(orderId)) {
        newSelectedItems = prev.filter(id => id !== orderId);
      } else {
        newSelectedItems = [...prev, orderId];
      }
      // Calculate the total fee based on newly selected items
      const selectedOrders = showOrders.filter(order => newSelectedItems.includes(order.uuid));
      const newTotal = calculateTotalFee(selectedOrders);
      setTotalAmount(newTotal);
      return newSelectedItems;
    });
  }

  const handleSelectAll = () => {
    let newSelectedItems;
    if (selectedItems.length === currentOrders.length) {
      newSelectedItems = [];
      setTotalAmount(0); // No items selected, total is 0
    } else {
      newSelectedItems = currentOrders.map(order => order.uuid);
      const newTotal = calculateTotalFee(currentOrders); // All items selected
      setTotalAmount(newTotal);
    }
    setSelectedItems(newSelectedItems);
  }

  // Per-row "mark cancel": route the order into the cancel tab (refund handled there).
  const requestCancel = (order) => setConfirm({
    message: `${order.smileNameInput || ''}\n${order.url || ''}\n\nรายการนี้ รายการจะเข้าสู่ขั้นตอนการยกเลิกและคืนเงิน ในหน้า รายการ -> ดำเนินการยกเลิก`,
    confirmText: 'ยกเลิกออเดอร์',
    onConfirm: async () => {
      // allowVoid: this is a deliberate single cancel → a still-voidable credit-card charge
      // routes into the void lane (unlike the one-click addTracking cancel).
      const ok = await cancelOrder({ orderId: order.id, allowVoid: true });
      if (ok) { toast.success('ส่งเข้ารายการยกเลิกแล้ว'); handleSuccessUpdate(); }
      else toast.error('ยกเลิกไม่สำเร็จ');
    },
  });

  // Bulk "mark cancel" — replaces the old direct-stamp mass buttons.
  const requestMassCancel = () => {
    if (!selectedItems || selectedItems.length === 0) { toast.error("No item selected."); return; }
    setConfirm({
      message: `ยกเลิก ${selectedItems.length} ออเดอร์ที่เลือก? ทั้งหมดจะเข้าสู่ขั้นตอนการคืนเงิน`,
      confirmText: 'ยกเลิกทั้งหมด',
      onConfirm: () => handleMassUpdate('cancel'),
    });
  };

  const handleMassUpdate = async (value) => {
    if (!selectedItems || selectedItems.length === 0) {
      toast.error("No item selected.");
      return; // Or handle this case more gracefully
    }
    const response = await massUpdate({uuids: selectedItems, value: value}, authState.user.id)
    if(response){
      toast.success("Data update successfully")
      handleSuccessUpdate()
    }else{
      toast.error("Update failed")
    }
  }

  const columns = [
      { key: 'smileNameInput', label: 'Smile Input' },
      { key: 'orderDate', label: 'Order Date' },
      { key: 'shipStatus', label: 'สถานะของ' },
      { key: 'productPayment', label: 'ค่าสินค้า' },
      { key: 'shipJpPaid', label: 'ค่าส่งญี่ปุ่น' },
      { key: 'weightPaid', label: 'ค่าน้ำหนัก' },
      { key: 'shipThPaid', label: 'ค่าส่งไทย' },
      { key: 'trackingJp', label: 'JP Tracking' },
      { key: 'amount', label: 'ราคา' },
      { key: 'qty', label: 'จำนวน' },
      { key: 'deliveryFeeJp', label: 'ค่าส่งญี่ปุ่น' },
      { key: 'deliveryFeeTh', label: 'ค่าส่งไทย' },
      { key: 'weight', label: 'น้ำหนัก' },
      { key: 'url', label: 'Url' },
      { key: 'shipDate', label: 'รอบเรือ' },
      { key: '', label: '' },
      // Add more columns as needed
    ];

    const renderTableHeader = () => {
      return (
        <tr style={{ position: 'sticky', top: 0, backgroundColor: 'white', zIndex: 1 }}>
          {multiSelect && <th className="text-left"><input type="checkbox" onChange={handleSelectAll} checked={selectedItems.length === showOrders.length} /></th>}
          {columns.map(column => <th key={column.key} className='text-left'>{column.label}</th>)}
        </tr>
      );
    };

    const shipStatusMapping = {
      'รอส่งในญี่ปุ่น': 'รอส่ง',
      'คลังญี่ปุ่น รอขึ้นเรือ': 'คลังญี่ปุ่น',
      'ระหว่างขนส่งในญี่ปุ่น': 'ระหว่างส่ง',
      'อยู่บนเรือ': 'อยู่บนเรือ',
      'คลังไทย': 'คลังไทย',
      'Cancel by store': 'Cancel'
    };

    const productPaymentMapping = {
      'รอชำระ': {value: <FaTimes color="red" /> },
      'คืนเงินแล้ว': { value: <span className='text-blue-600'>คืนแล้ว</span> },
      'มัดจำ 50%': { value: <span className='text-orange-600'>50</span> },
      'มัดจำ 30%': { value: <span className='text-orange-600'>30</span> },
      'ชำระครบแล้ว': { value: <FaCheck color="green" />},
    };
      
    const renderTableBody = () => {
      if(currentOrders && currentOrders.length > 0){
      return currentOrders.map(order => (
        <tr key={order.uuid} className={order.shipStatus === 'Cancel by store' ? 'bg-red-100' : ''}>
          {multiSelect && (
            <td>
              <input
                type="checkbox"
                checked={selectedItems.includes(order.uuid)}
                onChange={() => handleSelectItem(order.uuid)}
              />
            </td>
          )}
          <td>{order.smileNameInput}</td>
          <td>{formatDate(order.orderDate)}</td>
          <td>{order.shipStatus === 'อยู่บนเรือ' && order.ShipDate?.isAir
            ? 'อยู่ระหว่างส่งกลับไทย'
            : (shipStatusMapping[order.shipStatus] || order.shipStatus)}</td>
          <td>
            <div className="flex items-center">
              {productPaymentMapping[order.productPayment]?.value || order.productPayment}
            </div>
          </td>
          <td>
            <div className="flex items-center">
              {productPaymentMapping[order.shipJpPaid]?.value || order.shipJpPaid}
            </div>
          </td>
          <td>{order.weightPaid ? <FaCheck color="green" /> : <FaTimes color="red" />}</td>
          <td>{order.shipThPaid ? <FaCheck color="green" /> : <FaTimes color="red" />}</td>
          <td>{order.trackingJp}</td>
          <td>{order.amount}</td>
          <td>{order.qty}</td>
          <td>{order.deliveryFeeJp}</td>
          <td>{order.deliveryFeeTh}</td>
          <td>{order.weight}</td>
          <td className="">
            <div
              className="overflow-hidden overflow-ellipsis whitespace-nowrap"
              style={{ maxWidth: '100px' }}
              title={order.url}
            >
              <a href={order.url} target="_blank" rel="noopener noreferrer">
                {order.url}
              </a>
            </div>
          </td>
          <td>
            <span 
              className={`${
                order.ShipDate && order.ShipDate.isAir 
                  ? 'text-blue-600 font-semibold' 
                  : 'text-gray-700'
              }`}
            >
              {order.ShipDate ? formatDate(order.ShipDate.shipDate) : ""}
              {order.ShipDate && order.ShipDate.isAir && (
                <>
                  <span className="ml-1 text-xs text-blue-500">✈️</span>
                  {(order.ShipDate.warehouseReceivingAddress?.addressNickName ||
                    order.ShipDate.warehouseReceivingAddressId != null) && (
                    <span className="ml-1 text-xs text-blue-500">
                      ({order.ShipDate.warehouseReceivingAddress?.addressNickName || 'Special'})
                    </span>
                  )}
                </>
              )}
            </span>
          </td>
          <td className="cursor-pointer bg-blue-500 hover:bg-blue-600 text-center text-sm rounded-lg text-white" onClick={() => openForm(order)}>Detail</td>
          <td className="text-center">
            {!order.cancelStage && (
              <button className="bg-orange-500 hover:bg-orange-600 text-white text-sm rounded-lg px-2 py-1"
                onClick={() => requestCancel(order)}>ยกเลิก</button>
            )}
          </td>
        </tr>
      ));
    }
    };

    const openForm = async (order) => {
      setSelectedOrder(order);
      setIsFormOpen(true)
    };

    const closeForm = () =>{
      setSelectedOrder(null);
      setIsFormOpen(false)
    }

    const calculateTotalFee = (showOrders) => {
      let totalAmount = 0;
      if(showOrders && showOrders.length > 0){
        showOrders.forEach(order => {
          if(order.shipStatus != 'Cancel by store'){
            // For amount
            if (order.isDown) {
              totalAmount += order.amount / 2;
            } else {
              totalAmount += order.amount;
            }

            // For delivery fee
            if (order.jpShipIsDown) {
              totalAmount += order.deliveryFeeJp / 2;
            } else {
              totalAmount += order.deliveryFeeJp;
            }
          }
        }
      )};
      return totalAmount
    }

    const handleSelectChange = (e) => {
      const selectedValue = e.target.value;
      
      if (ordersPagination && onShipDateChange) {
        // Server-side filtering - notify parent to fetch filtered orders
        onShipDateChange(selectedValue);
      } else {
        // Client-side filtering (only when not using server-side pagination)
        if(selectedValue === 'all'){
          setShowOrders(orders || [])
          // When showing all orders, use accumulatedOrderAmount if available
          if (!multiSelect && accumulatedOrderAmount !== undefined && accumulatedOrderAmount !== null) {
            setTotalAmount(accumulatedOrderAmount);
          }
        }else if(selectedValue === 'none'){
          const filteredOrders = (orders || []).filter(order => order.shipDateId === null)
          setShowOrders(filteredOrders)
        }else{
          const filteredOrders = (orders || []).filter(order => order.shipDateId == selectedValue)
          setShowOrders(filteredOrders)
        }
      }
    }

    const handleCopyLink = () => {
      // Use orders when server-side pagination, otherwise use showOrders
      const ordersToCopy = ordersPagination ? orders : showOrders;
      if (!ordersToCopy || ordersToCopy.length === 0) {
        toast.error('No orders to copy');
        return;
      }
      const linkArray = ordersToCopy.map(order => order.url)
      const copyText = linkArray.join('\n')
      navigator.clipboard.writeText(copyText)
      .then(() => {
        toast.success('Copied!!')
      })
    }

    const handleCopyLinkAndBoxNo = async () => {
      // Use orders when server-side pagination, otherwise use showOrders
      const ordersToCopy = ordersPagination ? orders : showOrders;
      if (!ordersToCopy || ordersToCopy.length === 0) {
        toast.error('No orders to copy');
        return;
      }
      // Backend expects integer id, not uuid
      const idArray = ordersToCopy.map(order => order.id).filter(id => id != null && id !== undefined);
      if (idArray.length === 0) {
        toast.error('No valid order IDs found');
        return;
      }
      const response = await getBoxNosFromOrderIds(idArray)
      if (!response || !Array.isArray(response)) {
        toast.error('Failed to fetch box numbers');
        return;
      }
      let text = ''
      response.forEach(order => {
        text += order.url + '\n'
        if(order.WeeklyShips && order.WeeklyShips.length > 0){
          order.WeeklyShips.forEach((ship) => {
            text += ship.boxNo + " "; // Display each box number with its index
          });
        }else{
          text += 'ไม่พบเลขกล่อง'
        }
        text += '\n'
      });
      navigator.clipboard.writeText(text)
      .then(() => {
        toast.success('Copied!!')
      })
      .catch(() => {
        toast.error('Failed to copy to clipboard')
      })
    }

    return (
      <div className="pb-3">
        <Modal
          isOpen={isFormOpen}
          onRequestClose={closeForm}
          style={customStyles}
          ariaHideApp={false}
        >
          {selectedOrder && <OrderDetail order={selectedOrder} handleSuccessUpdate={handleSuccessUpdate}/>}
        </Modal>
        <ConfirmDialog
          open={!!confirm}
          title="ยืนยันการยกเลิกโดยผู้ขาย"
          message={confirm?.message}
          confirmText={confirm?.confirmText || 'ยืนยัน'}
          confirmColor="warning"
          onConfirm={() => { const cb = confirm?.onConfirm; setConfirm(null); if (cb) cb(); }}
          onClose={() => setConfirm(null)}
        />
        <div className="mt-8 flow-root">
          <div className="-mx-4 -my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
            <div className="inline-block min-w-full py-2 align-middle sm:px-6 lg:px-8">
              <div className='flex mb-2 -mt-2'>
                <ShipDateDropdown 
                  handleDataChange={handleSelectChange}
                  value={selectedShipDateId || 'all'}
                />
                <div>
                  <button className='btn-primary ml-3' onClick={toggleMultiSelect}>
                    {multiSelect ? 'Exit Multi-Select' : 'Mass Edit'}
                  </button>
                  {multiSelect && (
                    <button onClick={requestMassCancel} className='bg-orange-500 hover:bg-orange-600 text-white rounded px-3 py-1 mx-3'>
                      ยกเลิกที่เลือก
                    </button>
                  )}
                  <span className="border-l border-gray-800 ml-3 h-full">
                    <button className='btn-primary ml-3' onClick={handleCopyLink}>Copy Link</button>
                    <button className='btn-primary ml-3' onClick={handleCopyLinkAndBoxNo}>Copy Link & Box No</button>
                  </span>
                </div>
                <span className='ml-auto text-xl'>ยอดรวมทั้งหมด {formatAmount(totalAmount)} เยน</span>
              </div>
              <div className="overflow-hidden shadow ring-1 ring-black ring-opacity-5 sm:rounded-lg">
                <table className="min-w-full divide-y divide-gray-300">
                  <thead className="bg-gray-50">
                    {renderTableHeader()}
                  </thead>
                  <tbody className="divide-y divide-gray-200 bg-white">
                    {renderTableBody()}
                    
                  </tbody>
                </table>
                <div className="flex justify-center items-center space-x-2 mt-4">
                <button
                  className="px-4 py-2 bg-gray-300 hover:bg-gray-400 disabled:opacity-50"
                  onClick={() => paginate(currentPage - 1)}
                  disabled={currentPage === 1}
                >
                  Previous
                </button>
                {renderPageNumbers()}
                <button
                  className="px-4 py-2 bg-gray-300 hover:bg-gray-400 disabled:opacity-50"
                  onClick={() => paginate(currentPage + 1)}
                  disabled={currentPage >= totalPages}
                >
                  Next
                </button>
              </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    )
  }
  